
import Vue, { PropType } from 'vue';
import UpsLogo from '@/assets/svg-icons/ups-logo.svg';

interface UpsServiceBreakdown {
  serviceCode: string;
  serviceName: string;
  upsCode: string;
  packageCount: number;
  shipmentCount: number;
  totalCost: number;
}

interface UpsShipmentDetail {
  trackingNumber: string;
  serviceCode: string;
  upsCode: string;
  orderNumber: string;
  customerName: string | null;
  shippingCost: number;
  createdAt: string;
  // Multi-package fields
  packageSequence: number | null;
  totalPackages: number | null;
  isMultiPackage: boolean;
}

interface UpsPackageSummary {
  reportDate: string;
  accountNumber: string;
  accountName: string;
  shipperName: string;
  shipperStreet: string;
  shipperCity: string;
  shipperState: string;
  shipperPostalCode: string;
  totalPackages: number;
  totalShipments: number;
  totalCost: number;
  serviceBreakdown: UpsServiceBreakdown[];
  shipments: UpsShipmentDetail[];
  generatedAt: string;
  note: string | null;
}

export default Vue.extend({
  name: 'UpsPackageSummaryPrint',

  props: {
    summary: {
      type: Object as PropType<UpsPackageSummary>,
      required: true
    },
    showTrackingList: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      upsLogo: UpsLogo
    };
  },

  computed: {
    /**
     * Ensure all standard UPS service types are shown, even if count is 0
     */
    serviceBreakdownWithDefaults(): UpsServiceBreakdown[] {
      const defaultServices = [
        { upsCode: '1DA', serviceName: 'Next Day Air', serviceCode: 'ups_next_day_air' },
        { upsCode: '2DA', serviceName: '2nd Day Air', serviceCode: 'ups_2nd_day_air' },
        { upsCode: '3DS', serviceName: '3 Day Select', serviceCode: 'ups_3_day_select' },
        { upsCode: 'GND', serviceName: 'Ground', serviceCode: 'ups_ground' }
      ];

      const result: UpsServiceBreakdown[] = [];
      const existingCodes = new Set(this.summary.serviceBreakdown.map(s => s.upsCode));

      // Add existing services first
      for (const service of this.summary.serviceBreakdown) {
        result.push(service);
      }

      // Add default services with 0 count if not present
      for (const defaultSvc of defaultServices) {
        if (!existingCodes.has(defaultSvc.upsCode)) {
          result.push({
            serviceCode: defaultSvc.serviceCode,
            serviceName: defaultSvc.serviceName,
            upsCode: defaultSvc.upsCode,
            packageCount: 0,
            shipmentCount: 0,
            totalCost: 0
          });
        }
      }

      // Sort by standard order
      const order: Record<string, number> = { '1DA': 1, '2DA': 2, '3DS': 3, 'GND': 4 };
      return result.sort((a, b) => (order[a.upsCode] || 99) - (order[b.upsCode] || 99));
    },

    internationalCount(): number {
      // Count international shipments (WXS, WXP, WXD, WWS codes)
      const intlCodes = ['WXS', 'WXP', 'WXD', 'WWS'];
      return this.summary.serviceBreakdown
        .filter(s => intlCodes.includes(s.upsCode))
        .reduce((sum, s) => sum + s.packageCount, 0);
    }
  },

  methods: {
    formatDate(dateString: string): string {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric'
      }).toUpperCase();
    },

    formatDateTime(dateString: string): string {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    },

    /**
     * Trigger browser print dialog
     */
    print() {
      window.print();
    }
  }
});
